<template>
  <div class="studio-panel" style="max-width: 700px;">

    <!-- Top-level tabs — Daily Affirmation / Pro Settings, mirroring the
         Mystic sub-tab strip. -->
    <nav class="syn-tabs">
      <button
        v-for="t in TABS" :key="t.key"
        type="button" class="syn-tab"
        :class="{ active: tab === t.key, locked: t.key === 'pro' && !isSuper }"
        @click="tab = t.key"
      >
        <span v-if="t.key === 'pro' && !isSuper" class="syn-lock" aria-hidden="true">🔒</span>{{ t.label }}</button>
    </nav>

    <!-- Daily Affirmation — the slim default form, shown to every reader. -->
    <div v-show="tab === 'daily'">
      <DailyDropPanel />
    </div>

    <!-- Pro Settings — Drop Settings, Embed & Widgets, Partner API Keys as sub-tabs. -->
    <div v-show="tab === 'pro'">
      <!-- Locked: non-super readers see the value props instead of the config. -->
      <div v-if="!isSuper" class="pro-locked">
        <div class="pro-locked-head">
          <span class="pro-locked-lock" aria-hidden="true">🔒</span>
          <h3 class="pro-locked-title">Unlock Mystic Pro Syndication</h3>
        </div>
        <p class="pro-locked-intro">
          Turn your daily reading into a published, branded channel seekers
          follow anywhere. Pro Settings unlocks:
        </p>
        <ul class="pro-props">
          <li v-for="p in PRO_PROPS" :key="p.title" class="pro-prop">
            <span class="pro-prop-icon" aria-hidden="true">{{ p.icon }}</span>
            <span class="pro-prop-body">
              <span class="pro-prop-title">{{ p.title }}</span>
              <span class="pro-prop-desc">{{ p.desc }}</span>
            </span>
          </li>
        </ul>
      </div>

      <!-- Unlocked: the real configurator. -->
      <template v-else>
        <nav class="pro-subtabs">
          <button
            v-for="t in PRO_TABS" :key="t.key"
            type="button" class="pro-subtab"
            :class="{ on: proTab === t.key }"
            @click="proTab = t.key"
          >{{ t.label }}</button>
        </nav>
        <div class="pro-subpanel">
          <ProDropPanel v-if="proTab === 'drops'" />
          <SyndicationPanel v-else-if="proTab === 'embed'" />
          <PartnerAPIKeysPanel v-else-if="proTab === 'apikeys'" />
        </div>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { store } from '@/store'
import ProDropPanel from './ProDropPanel.vue'
import DailyDropPanel from './DailyDropPanel.vue'
import SyndicationPanel from './SyndicationPanel.vue'
import PartnerAPIKeysPanel from './PartnerAPIKeysPanel.vue'

const TABS = [
  { key: 'daily', label: 'Daily Affirmation' },
  { key: 'pro', label: 'Pro Settings' },
]
const PRO_TABS = [
  { key: 'drops', label: 'Drop Settings' },
  { key: 'embed', label: 'Embed & Widgets' },
  { key: 'apikeys', label: 'Partner API Keys' },
]
// What Pro unlocks — shown to non-super readers when they open the locked tab.
const PRO_PROPS = [
  { icon: '🌙', title: 'Drop Settings',
    desc: 'Publish a richer daily drop - choose Zodiac, Natal, or Community readings, blend in a tarot card, and shape its focus & tone in your own words. Simulate before it goes live.' },
  { icon: '🔮', title: 'Embed & Widgets',
    desc: 'Drop a branded horoscope widget onto your own site - your voice, your styling, updating every day on autopilot.' },
  { icon: '🔑', title: 'Partner API Keys',
    desc: 'Pull your readings into any app or partner integration through the Mystic AIda API.' },
]

// Full syndication tooling is gated to syndicate_enabled ("super") readers.
const isSuper = computed(() => !!store.reader?.syndicate_enabled)

const tab = ref('daily')
const proTab = ref('drops')
</script>

<style scoped>
@import '@/styles/studio-forms.css';

/* Top-level tab strip — mirrors the Mystic sub-tabs (profile-subtabs). */
.syn-tabs {
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
}
.syn-tab {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(195, 169, 255, 0.55);
  font-family: 'Cinzel', serif;
  font-size: 13px;
  letter-spacing: 0.4px;
  padding: 8px 16px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.syn-tab:hover { color: rgba(232, 216, 255, 0.9); }
.syn-tab.active { color: #C9A94E; border-bottom-color: #C9A94E; }
.syn-tab.locked { color: rgba(195, 169, 255, 0.4); }
.syn-tab.locked.active { color: #C9A94E; }
.syn-lock { font-size: 11px; margin-right: 5px; filter: grayscale(0.2); }

/* Locked value-prop view — shown to non-super readers on the Pro tab. */
.pro-locked {
  padding: 20px 22px 24px;
  border: 1px solid rgba(201, 169, 78, 0.28);
  border-radius: 14px;
  background: radial-gradient(600px 240px at 50% 0%, rgba(40, 22, 70, 0.5) 0%, rgba(10, 0, 21, 0.2) 75%);
}
.pro-locked-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.pro-locked-lock { font-size: 18px; }
.pro-locked-title {
  margin: 0;
  font-family: 'Cinzel', serif;
  font-size: 17px;
  color: #FFDFA3;
  font-weight: 600;
}
.pro-locked-intro {
  margin: 0 0 18px;
  font-size: 13.5px;
  line-height: 1.6;
  color: rgba(232, 216, 255, 0.78);
}
.pro-props { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.pro-prop { display: flex; gap: 12px; align-items: flex-start; }
.pro-prop-icon {
  flex: 0 0 auto;
  font-size: 18px;
  line-height: 1.4;
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(201, 169, 78, 0.12);
  border: 1px solid rgba(201, 169, 78, 0.3);
  border-radius: 8px;
}
.pro-prop-body { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.pro-prop-title {
  font-family: 'Cinzel', serif;
  font-size: 14px;
  color: #C9A94E;
  font-weight: 600;
}
.pro-prop-desc { font-size: 12.5px; line-height: 1.55; color: rgba(195, 169, 255, 0.72); }

/* Pro Settings sub-tabs */
.pro-subtabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 8px;
}
.pro-subtab {
  padding: 9px 14px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  font-family: 'Cinzel', serif;
  font-size: 12.5px;
  font-weight: 600;
  color: rgba(195, 169, 255, 0.55);
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.pro-subtab:hover { color: #E8D8FF; }
.pro-subtab.on { color: #C9A94E; border-bottom-color: #C9A94E; }

.studio-panel :deep(.studio-panel) { padding: 0; margin: 0; }
.studio-panel :deep(.studio-panel h2) { display: none; }
.studio-panel :deep(.studio-panel-subtitle) { display: none; }
</style>
